import type { KnowledgeBase, KnowledgeDocument } from '@ralph/contracts'
import { SURFACE_LABEL } from '@ralph/contracts'
import clsx from 'clsx'
import {
  CircleCheck,
  CircleDashed,
  Eye,
  FileText,
  FileUp,
  FolderSync,
  ShieldAlert,
  Trash2,
  TriangleAlert
} from 'lucide-react'
import { Fragment, type ReactNode, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isFileTooLarge, isQuotaRefusal, isRateLimited } from '@/api/knowledge-bases.ts'
import { Badge, Button, EmptyState, IconButton, Input, Panel, Spinner } from '@/components/ui/primitives.tsx'
import { useUploadAccept } from '@/hooks/use-api.ts'
import { useDeleteKnowledgeDocument, useUploadKnowledgeDocument } from '@/hooks/use-knowledge-bases.ts'
import { useI18n } from '@/i18n/index.ts'
import { tailOfPath } from './connected-folders.ts'
import { DocumentDrawer } from './DocumentDrawer.tsx'
import { baseRoom, healthSummary, type UploadOutcome, uploadOutcome, wasStored } from './knowledge-base.ts'
import {
  type DocumentFilter,
  diagnoseFailure,
  type IndexHealth,
  matchesFilter,
  needsAttention,
  type Standing,
  standingOf
} from './knowledge-console.ts'
import { asSentence, messageOf } from './knowledge-viewer.ts'
import { ProviderMark } from './ProviderMark.tsx'

/**
 * A knowledge base's documents (PRD-24, PRD-25, PRD-26).
 *
 * The table every reader of the base sees, and — for its maintainers — the way documents get in
 * and out: adding files within the base's limits (D-41), removing one, reading one again, and the
 * drawer with the passages search actually reads. While anything is still being read the list
 * polls (the hook's rule), so a verdict replaces "being read" without anybody reloading.
 *
 * ── Why the table scrolls in its own lane ──
 *
 * A failure message whose length is the server's choice once widened the table past a panel that
 * clipped it, and the row's own actions fell outside the box. So the table is `min-w` in an
 * `overflow-x-auto` lane, like every other table in the product, and anything long — a failure,
 * the removal's consequence — gets a full-width row of its own under the row it belongs to.
 */

// ---------------------------------------------------------------------------
// The health strip: one sentence, one bar, every segment a filter
// ---------------------------------------------------------------------------

/**
 * Colour only. The WORD beside each segment used to live here too, spread out of a module-scope
 * catalogue capture — the derived-constant half of PRD-27 F-3, which renders the previous
 * language after a switch with nothing failing. It is read from `t.health` in the body now.
 */
const STANDING_STYLE: Record<Standing, { bar: string; dot: string }> = {
  answering: { bar: 'bg-grounded', dot: 'bg-grounded' },
  working: { bar: 'bg-primary', dot: 'bg-primary' },
  silent: { bar: 'bg-alert/45', dot: 'bg-alert/45' },
  unreadable: { bar: 'bg-alert', dot: 'bg-alert' },
  quarantined: { bar: 'bg-alert', dot: 'bg-alert' }
}

const STANDING_ORDER: Standing[] = ['answering', 'working', 'silent', 'unreadable', 'quarantined']

/**
 * What the base is doing for the agents that use it, in one sentence and one bar. Every segment
 * is a document the table already holds, and every segment is a control: pressing one filters
 * the table to exactly those rows. The bar is drawn only when there is more than one standing to
 * compare — a single full-width bar is decoration that looks like a measurement.
 */
export const HealthStrip = ({
  health,
  loading,
  filter,
  synced,
  onFilter
}: {
  health: IndexHealth
  loading: boolean
  filter: DocumentFilter
  synced: number
  onFilter: (filter: DocumentFilter) => void
}) => {
  // First statement, ahead of the early return below: a hook cannot sit behind a branch.
  const fmt = useI18n()
  const t = fmt.t.knowledge

  if (loading) {
    return (
      <p className="mt-3.5 flex items-center gap-2 text-[12.5px] text-fg-subtle">
        <Spinner size={12} /> {t.base.counting}
      </p>
    )
  }

  const segments = STANDING_ORDER.map((standing) => ({ standing, count: health[standing] })).filter(
    (segment) => segment.count > 0
  )

  return (
    <div className="mt-3.5">
      <p
        className={clsx(
          'flex items-start gap-1.5 text-pretty text-[12.5px] leading-[1.45]',
          needsAttention(health) ? 'text-fg' : 'text-fg-muted'
        )}
      >
        {needsAttention(health) ? (
          <TriangleAlert size={12} strokeWidth={2.3} className="mt-[3px] shrink-0 text-alert" aria-hidden />
        ) : (
          <CircleCheck size={12} strokeWidth={2.3} className="mt-[3px] shrink-0 text-grounded" aria-hidden />
        )}
        <span>{healthSummary(fmt, health)}</span>
      </p>

      {segments.length > 1 && (
        <div className="mt-2.5 flex h-[6px] gap-[2px] overflow-hidden rounded-full" aria-hidden>
          {segments.map((segment) => (
            <div
              key={segment.standing}
              className={clsx('rounded-full', STANDING_STYLE[segment.standing].bar)}
              style={{ flexGrow: segment.count }}
            />
          ))}
        </div>
      )}

      {(segments.length > 1 || synced > 0) && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5">
          {segments.length > 1 &&
            segments.map((segment) => {
              const active = filter === segment.standing
              return (
                <button
                  key={segment.standing}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onFilter(active ? 'all' : segment.standing)}
                  className={clsx(
                    'flex items-center gap-1.5 rounded-full px-1.5 py-0.5 text-[11.5px] tracking-[-0.01em]',
                    'transition-colors duration-200',
                    active ? 'bg-card-solid font-semibold text-fg' : 'text-fg-muted hover:text-fg'
                  )}
                >
                  <span
                    className={clsx('h-[7px] w-[7px] shrink-0 rounded-full', STANDING_STYLE[segment.standing].dot)}
                    aria-hidden
                  />
                  {segment.count} {t.health[segment.standing]}
                </button>
              )
            })}
          {/* Not a standing: where a document came from is orthogonal to whether it answers,
              which is why the chip carries a glyph instead of a dot in the bar. */}
          {synced > 0 && (
            <button
              type="button"
              aria-pressed={filter === 'synced'}
              onClick={() => onFilter(filter === 'synced' ? 'all' : 'synced')}
              className={clsx(
                'flex items-center gap-1.5 rounded-full px-1.5 py-0.5 text-[11.5px] tracking-[-0.01em]',
                'transition-colors duration-200',
                filter === 'synced' ? 'bg-card-solid font-semibold text-fg' : 'text-fg-muted hover:text-fg'
              )}
            >
              <FolderSync size={10} strokeWidth={2.2} className="shrink-0" aria-hidden />
              {synced} {fmt.t.knowledgeFolders.document.fromFolder}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

export const DocumentsTab = ({
  base,
  canMaintain,
  items,
  loading,
  failed,
  gated,
  onOpenDocuments,
  filter,
  onFilter,
  uploadMaxBytes
}: {
  base: KnowledgeBase
  canMaintain: boolean
  items: KnowledgeDocument[]
  loading: boolean
  failed: boolean
  /** An administrator who cannot otherwise use the base has not opened its documents yet (D-18). */
  gated: boolean
  onOpenDocuments: () => void
  filter: DocumentFilter
  onFilter: (filter: DocumentFilter) => void
  uploadMaxBytes: number | null
}) => {
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const remove = useDeleteKnowledgeDocument(base.id)
  const uploader = useUploader(base, uploadMaxBytes)
  const [query, setQuery] = useState('')
  const [confirming, setConfirming] = useState<string | null>(null)
  const [removed, setRemoved] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const shown = items.filter((document) => matchesFilter(document, filter, query))
  const columns = canMaintain ? 6 : 5
  const filtered = filter !== 'all' || query.trim() !== ''

  const openDocument = (id: string | null) => {
    const params = new URLSearchParams(searchParams)
    if (id) params.set('doc', id)
    else params.delete('doc')
    setSearchParams(params, { replace: true })
  }
  const open = items.find((document) => document.id === searchParams.get('doc')) ?? null

  const removeDocument = (document: KnowledgeDocument) => {
    setConfirming(null)
    setRemoved(null)
    remove.mutate(document.id, { onSuccess: () => setRemoved(document.title) })
  }

  if (gated) {
    return (
      <Panel>
        <EmptyState
          title={t.documents.adminGateTitle}
          body={t.documents.adminGateBody}
          action={
            <Button variant="primary" icon={<Eye size={13} aria-hidden />} onClick={onOpenDocuments}>
              {t.documents.adminGateOpen}
            </Button>
          }
        />
      </Panel>
    )
  }

  return (
    <Panel className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 border-b border-rule px-4 py-3">
        <FileText size={12} strokeWidth={2.1} className="text-fg-muted" aria-hidden />
        <h3 className="eyebrow">{t.documents.heading}</h3>
        {!loading && (
          <span className="readout text-fg-muted">{t.documents.count(fmt, shown.length, items.length, filtered)}</span>
        )}
        {filtered && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              onFilter('all')
              setQuery('')
            }}
          >
            {t.documents.clearFilter}
          </Button>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* A filter that appears at eight rows: below that it is a control asking to be used
              on a list the eye has already finished reading. */}
          {items.length > 8 && (
            <div className="w-[12.5rem] max-w-full">
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t.documents.filterPlaceholder}
                aria-label={t.documents.filterLabel}
              />
            </div>
          )}
          {canMaintain && <UploadButton base={base} uploader={uploader} />}
        </div>
      </div>

      {canMaintain && <UploadLimits base={base} />}
      {canMaintain && <UploadReport uploader={uploader} />}

      {/* Mounted empty, so the removal is announced when it lands rather than never. */}
      <p role="status" className="sr-only">
        {removed ? t.documents.removed(removed) : ''}
      </p>

      {loading ? (
        <p className="flex items-center gap-2 px-4 py-6 text-[13px] text-fg-subtle">
          <Spinner size={12} /> {t.documents.loading}
        </p>
      ) : failed ? (
        <EmptyState title={t.documents.failed} body={t.base.failedBody} />
      ) : items.length === 0 ? (
        <EmptyState title={t.documents.empty} body={t.documents.emptyBody(canMaintain)} />
      ) : shown.length === 0 ? (
        <EmptyState title={t.documents.nothingMatches} body={t.documents.nothingMatchesBody} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-rule text-left">
                <th scope="col" className="eyebrow whitespace-nowrap px-4 py-2.5 font-normal">
                  {t.documents.columns.document}
                </th>
                <th scope="col" className="eyebrow whitespace-nowrap px-2 py-2.5 font-normal">
                  {t.documents.columns.status}
                </th>
                <th scope="col" className="eyebrow whitespace-nowrap px-2 py-2.5 text-right font-normal">
                  {t.documents.columns.passages}
                </th>
                <th scope="col" className="eyebrow whitespace-nowrap px-2 py-2.5 text-right font-normal">
                  {t.documents.columns.size}
                </th>
                <th scope="col" className="eyebrow whitespace-nowrap px-2 py-2.5 text-right font-normal">
                  {t.documents.columns.indexed}
                </th>
                {canMaintain && (
                  <th scope="col" className="w-[52px] px-2 py-2.5">
                    <span className="sr-only">{t.documents.actions}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {shown.map((document) => (
                <DocumentRows
                  key={document.id}
                  document={document}
                  columns={columns}
                  canMaintain={canMaintain}
                  armed={confirming === document.id}
                  onArm={(armed) => setConfirming(armed ? document.id : null)}
                  onRemove={() => removeDocument(document)}
                  onOpen={() => openDocument(document.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* The endpoint returns the 500 newest and says nothing about a 501st. A list that
          silently ends is a catalogue somebody believes they have seen all of. */}
      {items.length === 500 && (
        <p className="readout border-t border-rule px-4 py-2.5 text-fg-subtle">{t.documents.newest}</p>
      )}

      {remove.isError && (
        <p role="alert" className="border-t border-rule px-4 py-2.5 text-[12.5px] leading-relaxed text-alert">
          {t.documents.notRemoved(asSentence(messageOf(remove.error, t.genericError)))}
        </p>
      )}

      {open && (
        <DocumentDrawer
          document={open}
          canManage={canMaintain}
          onClose={() => openDocument(null)}
          onRemove={() => {
            openDocument(null)
            removeDocument(open)
          }}
        />
      )}
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// One document: its row, and the full-width row under it when there is something to say
// ---------------------------------------------------------------------------

const DocumentRows = ({
  document,
  columns,
  canMaintain,
  armed,
  onArm,
  onRemove,
  onOpen
}: {
  document: KnowledgeDocument
  columns: number
  canMaintain: boolean
  armed: boolean
  onArm: (armed: boolean) => void
  onRemove: () => void
  onOpen: () => void
}) => {
  // Inside the body, never at module scope: a capture taken at import freezes the locale and
  // then renders the previous language with no error at all (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const standing = standingOf(document)
  const synced = Boolean(document.origin?.folderId)
  const surface = document.origin?.surface ? SURFACE_LABEL[document.origin.surface] : t.documents.connectedFolder

  return (
    <Fragment>
      <tr className="border-t border-rule align-top transition-colors duration-200 first:border-t-0 hover:bg-card-solid">
        <td className="px-4 py-3">
          <span className="flex items-start gap-2.5">
            <FileText size={14} strokeWidth={1.9} className="mt-px shrink-0 text-grounded" aria-hidden />
            {/* A ceiling in rem, not `max-w-full`: `truncate` is `nowrap`, and in an auto-layout
                table the longest filename would otherwise set the column's width by itself. */}
            <span className="min-w-0 max-w-[19rem]">
              {/* The title is the way in: a button, so the row has one name and one focus stop and
                  does not steal the text selection someone needs to copy a filename. */}
              <button
                type="button"
                onClick={onOpen}
                className="block max-w-full truncate text-left font-medium tracking-[-0.014em] underline decoration-transparent underline-offset-2 transition-colors duration-200 hover:decoration-current"
              >
                {document.title}
              </button>
              <span className="readout block truncate text-fg-subtle">
                {document.filename}
                {document.language ? ` · ${document.language}` : ''}
                {document.pageCount ? ` · ${document.pageCount}p` : ''}
              </span>
              {/* Where it came from, left-truncated: the head of a path repeats on every row and
                  the tail is what tells two files apart. */}
              {document.origin && (
                <span
                  className="readout mt-0.5 flex items-center gap-1 truncate text-fg-subtle"
                  title={document.origin.path}
                >
                  <ProviderMark
                    surface={document.origin.surface}
                    provider={document.origin.provider}
                    size={10}
                    className="shrink-0"
                  />
                  <span className="truncate">
                    {surface} · {tailOfPath(document.origin.path)}
                  </span>
                </span>
              )}
            </span>
          </span>
        </td>

        <td className="whitespace-nowrap px-2 py-3">
          <StandingBadge document={document} />
        </td>
        <td className="readout whitespace-nowrap px-2 py-3 text-right">{document.chunkCount}</td>
        <td className="readout whitespace-nowrap px-2 py-3 text-right">{fmt.formatBytes(document.sizeBytes)}</td>
        <td className="readout whitespace-nowrap px-2 py-3 text-right text-fg-subtle">
          {document.indexedAt ? fmt.formatAgo(document.indexedAt) : '—'}
        </td>

        {canMaintain && (
          <td className="px-2 py-3 text-right">
            <IconButton
              label={t.documents.remove(document.title)}
              size={30}
              tone="alert"
              aria-expanded={armed}
              onClick={() => onArm(!armed)}
            >
              <Trash2 size={12} aria-hidden />
            </IconButton>
          </td>
        )}
      </tr>

      {/* Removal states its consequence at full width, rather than happening on one unguarded
          click at the end of a row. */}
      {armed && (
        <AnnotationRow columns={columns} tone="alert">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[12.5px] leading-relaxed text-fg-muted">
            <p className="flex min-w-[16rem] flex-1 items-start gap-2">
              <TriangleAlert size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
              {synced && document.origin ? (
                <span>
                  {fmt.t.knowledgeFolders.document.removeConfirm(
                    fmt,
                    document.title,
                    surface,
                    tailOfPath(document.origin.path, 64),
                    document.chunkCount
                  )}
                </span>
              ) : (
                <span>
                  <span className="font-semibold text-fg">{t.documents.removeTitle(document.title)}</span>{' '}
                  {t.documents.removePassages(fmt, document.chunkCount)} {t.documents.removeFinal}
                </span>
              )}
            </p>
            <span className="ml-auto flex shrink-0 items-center gap-1.5">
              <Button size="sm" variant="danger" autoFocus onClick={onRemove}>
                {synced ? fmt.t.knowledgeFolders.document.removeAndLeaveOut : t.documents.removeForGood}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onArm(false)}>
                {t.documents.keep}
              </Button>
            </span>
          </div>
        </AnnotationRow>
      )}

      {!armed && standing === 'unreadable' && <FailureRow document={document} columns={columns} onDetails={onOpen} />}

      {!armed && standing === 'quarantined' && (
        <AnnotationRow columns={columns} tone="alert">
          <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-fg-muted">
            <ShieldAlert size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
            <span>
              <span className="font-semibold text-fg">{t.documents.quarantined}</span> {t.documents.quarantinedBody}
            </span>
          </p>
        </AnnotationRow>
      )}

      {!armed && standing === 'silent' && (
        <AnnotationRow columns={columns} tone="alert">
          <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-fg-muted">
            <CircleDashed size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
            <span>
              <span className="font-semibold text-fg">{t.documents.silent}</span> {t.documents.silentBody}
            </span>
          </p>
        </AnnotationRow>
      )}
    </Fragment>
  )
}

/** A full-width note under the row it belongs to, sharing that row's grid and disturbing no column. */
const AnnotationRow = ({ columns, tone, children }: { columns: number; tone?: 'alert'; children: ReactNode }) => (
  <tr className={clsx(tone === 'alert' && 'bg-alert-soft')}>
    <td colSpan={columns} className="px-4 pb-3 pt-0.5">
      {children}
    </td>
  </tr>
)

const FailureRow = ({
  document,
  columns,
  onDetails
}: {
  document: KnowledgeDocument
  columns: number
  onDetails: () => void
}) => {
  const t = useI18n().t.knowledge
  const diagnosis = diagnoseFailure(document.statusDetail)
  return (
    <AnnotationRow columns={columns} tone="alert">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <p className="flex min-w-[16rem] flex-1 items-start gap-2 text-pretty text-[12.5px] leading-relaxed text-fg-muted">
          <TriangleAlert size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
          <span>
            <span className="font-semibold text-fg">{t.documents.couldNotRead}</span>{' '}
            {/* The remedy first when there is one — the server's sentence is the record, not the
                instructions — and the record under it, clamped: the drawer has it whole. */}
            {diagnosis.remedy ? t.documents.remedy[diagnosis.remedy] : t.documents.parserWords}
            {document.statusDetail && (
              <span className="readout mt-1 line-clamp-2 block break-words text-alert">{document.statusDetail}</span>
            )}
          </span>
        </p>
        <Button size="sm" variant="secondary" className="shrink-0" onClick={onDetails}>
          {t.documents.fullReason}
          <span className="sr-only"> — {document.title}</span>
        </Button>
      </div>
    </AnnotationRow>
  )
}

/**
 * The status cell: every state carries its word as well as its colour (AGM-124), and the ones
 * that mean "cannot be cited" carry a glyph too — `quarantined` and `chunking` must never share
 * a colour, because one is a file being worked on and the other a file the scan refused.
 */
const StandingBadge = ({ document }: { document: KnowledgeDocument }) => {
  const t = useI18n().t.knowledge
  const standing = standingOf(document)
  const label = t.status[document.status]
  if (standing === 'answering') {
    return (
      <Badge tone="grounded">
        <CircleCheck size={9} strokeWidth={2.8} aria-hidden />
        {label}
      </Badge>
    )
  }
  if (standing === 'unreadable') {
    return (
      <Badge tone="alert">
        <TriangleAlert size={9} strokeWidth={2.8} aria-hidden />
        {label}
      </Badge>
    )
  }
  if (standing === 'quarantined') {
    return (
      <Badge tone="alert">
        <ShieldAlert size={9} strokeWidth={2.8} aria-hidden />
        {label}
      </Badge>
    )
  }
  if (standing === 'silent') {
    return (
      <Badge tone="alert">
        <CircleDashed size={9} strokeWidth={2.8} aria-hidden />
        {t.documents.noPassagesBadge}
      </Badge>
    )
  }
  return (
    <Badge tone="primary">
      <Spinner size={9} />
      {label}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Adding documents, within the base's limits (D-41)
// ---------------------------------------------------------------------------

/** How full the base is, in words, for the people who can add to it; nothing when unlimited. */
const UploadLimits = ({ base }: { base: KnowledgeBase }) => {
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const room = baseRoom(base)
  if (base.limits === null || room === null) return null
  return (
    <div className="border-b border-rule px-4 py-2">
      <p className="readout text-fg-subtle">
        {t.upload.limits(
          fmt,
          base.documentCount,
          base.limits.documents,
          fmt.formatBytes(base.storageBytes),
          fmt.formatBytes(base.limits.storageBytes)
        )}
      </p>
      {room.full && (
        <p className="mt-1 flex items-start gap-1.5 text-pretty text-[12.5px] leading-[1.45] text-alert">
          <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
          {t.upload.full}
        </p>
      )}
    </div>
  )
}

type UploadLine = { name: string; outcome: UploadOutcome | 'refused'; detail: string | null }

/**
 * Add one file or several. They go one at a time, each answered on its own: a file that was read
 * and could not be used is still added (its row says why), a refusal names the file and gives
 * the server's own sentence, and a limit reached stops the rest — every one after it would be
 * refused for the same reason — and says how many were not tried.
 */
const useUploader = (base: KnowledgeBase, uploadMaxBytes: number | null) => {
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const upload = useUploadKnowledgeDocument(base.id)
  const [progress, setProgress] = useState<{ index: number; total: number; name: string } | null>(null)
  const [lines, setLines] = useState<UploadLine[]>([])
  const [notTried, setNotTried] = useState(0)

  const refusal = (error: unknown, name: string): string => {
    if (isFileTooLarge(error) && uploadMaxBytes !== null)
      return t.upload.tooLarge(name, fmt.formatBytes(uploadMaxBytes))
    if (isRateLimited(error)) return `${t.upload.notAdded(name)} ${t.upload.rateLimited}`
    return `${t.upload.notAdded(name)} ${asSentence(messageOf(error, t.genericError))}`
  }

  const run = async (files: File[]) => {
    setLines([])
    setNotTried(0)
    const done: UploadLine[] = []
    for (const [index, file] of files.entries()) {
      setProgress({ index: index + 1, total: files.length, name: file.name })
      try {
        const result = await upload.mutateAsync(file)
        done.push({ name: file.name, outcome: uploadOutcome(result), detail: result.detail ?? null })
        setLines([...done])
      } catch (error) {
        done.push({ name: file.name, outcome: 'refused', detail: refusal(error, file.name) })
        setLines([...done])
        if (isQuotaRefusal(error) || isRateLimited(error)) {
          setNotTried(files.length - index - 1)
          break
        }
      }
    }
    setProgress(null)
  }

  const clear = () => {
    setLines([])
    setNotTried(0)
  }

  return { run, progress, lines, notTried, clear }
}

type Uploader = ReturnType<typeof useUploader>

const UploadButton = ({ base, uploader }: { base: KnowledgeBase; uploader: Uploader }) => {
  const t = useI18n().t.knowledge
  const accept = useUploadAccept()
  const inputRef = useRef<HTMLInputElement>(null)
  const room = baseRoom(base)
  return (
    <>
      {/* `hidden`, not `sr-only`: a visually hidden file input is still in the accessibility tree,
          and a screen reader would find a nameless "Choose File" beside "Add documents". */}
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept={accept}
        onChange={(event) => {
          const files = [...(event.target.files ?? [])]
          event.target.value = ''
          if (files.length > 0) void uploader.run(files)
        }}
      />
      <Button
        size="sm"
        variant="primary"
        icon={<FileUp size={13} aria-hidden />}
        onClick={() => inputRef.current?.click()}
        disabled={uploader.progress !== null || (room?.full ?? false)}
      >
        {t.upload.add}
      </Button>
    </>
  )
}

/** What the last upload did, under the toolbar: progress, then one line per file worth a line. */
const UploadReport = ({ uploader }: { uploader: Uploader }) => {
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const { progress, lines, notTried } = uploader
  const stored = lines.filter((line) => wasStored(line.outcome)).length
  const reading = lines.some((line) => line.outcome === 'reading')
  const notes = lines.filter((line) => line.outcome !== 'added' && line.outcome !== 'reading')
  const refused = notes.some((line) => line.outcome === 'refused')
  const visible = progress !== null || lines.length > 0

  return (
    // Mounted empty rather than conditionally, so each outcome is announced as it lands.
    <div role="status" className={clsx(visible && 'border-b border-rule px-4 py-2.5')}>
      {progress && (
        <p className="readout flex items-center gap-1.5 text-fg-subtle">
          <Spinner size={11} />
          {t.upload.adding(fmt, progress.index, progress.total, progress.name)}
        </p>
      )}
      {!progress && lines.length > 0 && (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5">
          <div className="min-w-[14rem] flex-1 space-y-1">
            {stored > 0 && (
              <p className="flex items-center gap-1.5 text-[12.5px] text-fg-muted">
                <CircleCheck size={12} strokeWidth={2.3} className="shrink-0 text-grounded" aria-hidden />
                {t.upload.added(fmt, stored)} {reading ? t.upload.stillReading : ''}
              </p>
            )}
            {notes.map((line, index) => (
              <p
                // biome-ignore lint/suspicious/noArrayIndexKey: two files may share a name; the order is the identity here.
                key={`${line.name}-${index}`}
                className={clsx(
                  'flex items-start gap-1.5 text-pretty text-[12.5px] leading-[1.45]',
                  line.outcome === 'reused' ? 'text-fg-muted' : 'text-alert'
                )}
              >
                {line.outcome !== 'reused' && (
                  <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
                )}
                <span>
                  {line.outcome === 'reused'
                    ? t.upload.reused(line.name)
                    : line.outcome === 'unreadable'
                      ? t.upload.unreadable(line.name)
                      : line.outcome === 'quarantined'
                        ? t.upload.quarantined(line.name)
                        : line.detail}
                </span>
              </p>
            ))}
            {notTried > 0 && (
              <p className="text-[12.5px] leading-[1.45] text-alert">{t.upload.notTried(fmt, notTried)}</p>
            )}
            {refused && <p className="text-[12.5px] leading-[1.45] text-fg-muted">{t.upload.unchanged}</p>}
          </div>
          <Button size="sm" variant="ghost" onClick={uploader.clear}>
            {t.upload.clear}
          </Button>
        </div>
      )}
    </div>
  )
}
